import {
  Check, Repeat, Users, AlertTriangle,
  ExternalLink, Skull, Target, Zap,
} from 'lucide-react';
import { TYPE_ICON, PRIORITY_COLOR, getDeferralStyle } from './decisionConstants';
import { getNextBestAction, toneToColor } from '../../utils/nextBestAction';
import { InlineEditable } from '../common/InlineEditable';
import { isDecisionOverdue } from '../../utils/pendency';
import { formatMoneyMinor } from '../../utils/money';
import { SubtaskProgressRing } from '../shared/SubtaskProgressRing';

export default function DecisionRow({
  decision, globalIdx, selectedIdx,
  selectMode, isChecked, onToggleCheck, isFocused,
  onToggleComplete, onSelect, onDefer, onSchedule, onFocus, onDelegate,
  onCallAction, onPaymentAction, onReadingAction, onArchive, onRename,
}) {
  const Icon = TYPE_ICON[decision.type] || Target;
  const prColor = PRIORITY_COLOR[decision.priority] || 'var(--pd-subtle)';
  const isTask = decision.type === 'task';
  const isSelected = globalIdx === selectedIdx;
  const isRecurring = decision.recurrenceRule && decision.recurrenceRule !== 'NONE';
  const isDelegated = decision.delegatedTo;
  const deferralCount = decision.deferralCount || 0;
  const deferralDecay = getDeferralStyle(deferralCount);
  const isGraveyard = decision.category === 'GRAVEYARD';
  const isCompleted = decision.category === 'COMPLETED';
  const isOverdue = isDecisionOverdue(decision);
  const nba = isTask && !isCompleted && !isGraveyard ? getNextBestAction(decision) : null;

  let completedAgo = '';
  if (isCompleted && decision.completedAt) {
    const diff = Date.now() - new Date(decision.completedAt).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) completedAgo = 'just now';
    else if (hours < 24) completedAgo = `${hours}h ago`;
    else completedAgo = `${Math.floor(hours / 24)}d ago`;
  }

  return (
    <div
      key={`${decision.type}-${decision.id}`}
      data-testid={`decision-item-${decision.id}`}
      data-decision-idx={globalIdx}
      className={`flex items-center gap-3 px-5 py-2.5 group cursor-pointer decision-row ${isCompleted ? 'completed-item-strike' : ''} ${isOverdue ? 'pd-pendency-pulse' : ''} ${isFocused ? 'pd-focus-active' : ''}`}
      style={{
        borderBottom: '1px solid var(--pd-border-subtle)',
        background: isSelected ? 'var(--pd-panel-2)' : isCompleted ? 'rgba(21,128,61,0.02)' : deferralDecay.background,
        outline: isSelected ? '2px solid var(--pd-focus)' : 'none',
        outlineOffset: '-2px', borderRadius: isSelected ? '8px' : undefined,
        ...(isCompleted ? {} : deferralDecay),
        opacity: isGraveyard ? 0.55 : 1,
      }}
      onClick={() => {
        if (selectMode) {
          onToggleCheck?.(decision);
        } else if (!isCompleted) {
          onSelect?.(decision);
        }
      }}
    >
      {/* Checkbox for select mode */}
      {selectMode && (
        <div
          data-testid={`decision-checkbox-${decision.id}`}
          className="w-[18px] h-[18px] rounded border-2 flex items-center justify-center flex-shrink-0 cursor-pointer transition-all"
          style={{
            borderColor: isChecked ? 'var(--pd-focus)' : 'var(--pd-border)',
            background: isChecked ? 'var(--pd-focus)' : 'transparent',
          }}
          onClick={(e) => { e.stopPropagation(); onToggleCheck?.(decision); }}
        >
          {isChecked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
        </div>
      )}

      {/* Left action - hidden in select mode */}
      {!selectMode && (isCompleted ? (
        <div className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--pd-success)' }}>
          <Check className="w-3 h-3 text-white" strokeWidth={3} />
        </div>
      ) : isTask ? (
        <button data-testid={`decision-complete-${decision.id}`}
          onClick={(e) => { e.stopPropagation(); onToggleComplete?.(decision); }}
          className="w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform"
          style={{ borderColor: decision.businessColor || prColor }}>
        </button>
      ) : (
        <button
          data-testid={`decision-action-${decision.id}`}
          onClick={(e) => {
            e.stopPropagation();
            if (decision.type === 'call') onCallAction?.(decision, 'complete');
            else if (decision.type === 'payment') onPaymentAction?.(decision, 'paid');
            else if (decision.type === 'reading') onReadingAction?.(decision, 'read');
          }}
          className="w-[18px] h-[18px] rounded flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform"
          style={{ background: `${prColor}15`, color: prColor }}>
          <Icon className="w-3 h-3" />
        </button>
      ))}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {!isTask && !isCompleted && <Icon className="w-3 h-3 flex-shrink-0" style={{ color: prColor }} />}
          {isCompleted && decision.type !== 'task' && <Icon className="w-3 h-3 flex-shrink-0 item-meta-text" style={{ color: 'var(--pd-success)' }} />}
          <span className={`text-[12px] font-medium truncate ${isCompleted ? 'item-title-text' : ''}`}
            style={{ color: (isCompleted || isGraveyard) ? 'var(--pd-muted)' : 'var(--pd-text)', letterSpacing: '-0.01em', textDecoration: isGraveyard ? 'line-through' : 'none' }}>
            {onRename && !isCompleted && !isGraveyard ? (
              <InlineEditable
                value={decision.title}
                testId={`decision-rename-${decision.id}`}
                ariaLabel={`Rename ${decision.type}`}
                onSave={(next) => onRename(decision, next)}
              />
            ) : (
              decision.title
            )}
          </span>
          {nba && (
            <span
              data-testid={`decision-nba-${decision.id}`}
              className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-[1px] rounded-full flex-shrink-0"
              style={{ background: `${toneToColor(nba.tone)}14`, color: toneToColor(nba.tone) }}
              title={nba.text}
            >
              <Zap className="w-2.5 h-2.5" />
              {nba.text}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          {decision.businessShortCode && (
            <span className={`text-[9px] px-1.5 py-[1px] rounded-full font-semibold ${isCompleted ? 'item-meta-text' : ''}`}
              style={{ background: `${decision.businessColor}15`, color: decision.businessColor }}>
              {decision.businessShortCode}
            </span>
          )}
          {isCompleted ? (
            <span className="completed-badge">
              <Check className="w-2.5 h-2.5" />{completedAgo}
            </span>
          ) : (
            <>
              {!isTask && (
                <span className="text-[8px] px-1.5 py-[1px] rounded-full font-semibold uppercase tracking-wider"
                  style={{ background: `${prColor}10`, color: prColor }}>
                  {decision.type}
                </span>
              )}
              <span className="text-[9px] font-medium" style={{ color: prColor }}>{decision.priority}</span>
              <span className="text-[9px] tabular-nums" style={{ color: 'var(--pd-subtle)' }}>{decision.estimatedMinutes}m</span>
              <SubtaskProgressRing subtasks={decision.subtasks} testId={`decision-subtask-ring-${decision.id}`} />
              {isRecurring && <Repeat className="w-3 h-3" style={{ color: 'var(--pd-focus)' }} />}
              {isDelegated && (
                <span className="text-[8px] px-1 py-[1px] rounded font-semibold flex items-center gap-0.5"
                  style={{ background: 'rgba(180,83,9,0.08)', color: 'var(--pd-warning)' }}>
                  <Users className="w-2.5 h-2.5" />{decision.delegatedTo}
                </span>
              )}
              {decision.type === 'payment' && decision.amountMinor != null && (
                <span className="text-[9px] tabular-nums font-medium" style={{ color: 'var(--pd-text)' }}>
                  {formatMoneyMinor(decision.amountMinor, decision.currency)}
                </span>
              )}
              {deferralCount >= 2 && (
                <span data-testid={`deferral-badge-${decision.id}`}
                  className="text-[8px] px-1.5 py-[1px] rounded-full font-bold flex items-center gap-0.5"
                  style={{
                    background: deferralCount >= 5 ? 'rgba(180,35,58,0.12)' : deferralCount >= 3 ? 'rgba(180,83,9,0.12)' : 'rgba(180,83,9,0.08)',
                    color: deferralCount >= 5 ? 'var(--pd-crimson)' : 'var(--pd-warning)',
                  }}>
                  {deferralCount >= 5 ? <Skull className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
                  {deferralCount}x deferred
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Always-visible "complete" action ─────────────────────────────
          The left circle/icon is too subtle for new users — they were
          struggling to find a Done button. We promote the primary
          complete-action for each type to an always-visible labelled
          button on the right edge of the row. The rest of the per-type
          actions (Slot / Defer / Focus / Delegate / Open) stay
          hover-revealed for advanced users. */}
      {!isCompleted && !isGraveyard && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {isTask ? (
            <button data-testid={`decision-done-${decision.id}-primary`}
              onClick={(e) => { e.stopPropagation(); onToggleComplete?.(decision); }}
              className="pd-decision-done-btn px-2.5 py-1 rounded-lg text-[10px] font-semibold inline-flex items-center gap-1"
              style={{ color: 'var(--pd-success)', background: 'var(--pd-green-soft)' }}>
              <Check className="w-3 h-3" />Done
            </button>
          ) : decision.type === 'call' ? (
            <button data-testid={`decision-done-${decision.id}-primary`}
              onClick={(e) => { e.stopPropagation(); onCallAction?.(decision, 'complete'); }}
              className="pd-decision-done-btn px-2.5 py-1 rounded-lg text-[10px] font-semibold inline-flex items-center gap-1"
              style={{ color: 'var(--pd-success)', background: 'var(--pd-green-soft)' }}>
              <Check className="w-3 h-3" />Done
            </button>
          ) : decision.type === 'payment' ? (
            <button data-testid={`decision-paid-${decision.id}-primary`}
              onClick={(e) => { e.stopPropagation(); onPaymentAction?.(decision, 'paid'); }}
              className="pd-decision-done-btn px-2.5 py-1 rounded-lg text-[10px] font-semibold inline-flex items-center gap-1"
              style={{ color: 'var(--pd-success)', background: 'var(--pd-green-soft)' }}>
              <Check className="w-3 h-3" />Paid
            </button>
          ) : decision.type === 'reading' ? (
            <button data-testid={`decision-read-${decision.id}-primary`}
              onClick={(e) => { e.stopPropagation(); onReadingAction?.(decision, 'read'); }}
              className="pd-decision-done-btn px-2.5 py-1 rounded-lg text-[10px] font-semibold inline-flex items-center gap-1"
              style={{ color: 'var(--pd-success)', background: 'var(--pd-green-soft)' }}>
              <Check className="w-3 h-3" />Read
            </button>
          ) : null}
        </div>
      )}

      {/* Secondary actions — hover-revealed for advanced workflows */}
      {!isCompleted && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          {isGraveyard ? (
            <>
              <button data-testid={`decision-revive-${decision.id}`}
                onClick={(e) => { e.stopPropagation(); onSchedule?.(decision); }}
                className="px-2 py-1 rounded-lg text-[10px] font-semibold"
                style={{ color: 'var(--pd-focus)', background: 'var(--pd-blue-soft)' }}>Revive</button>
              <button data-testid={`decision-archive-${decision.id}`}
                onClick={(e) => { e.stopPropagation(); onArchive?.(decision); }}
                className="px-2 py-1 rounded-lg text-[10px] font-semibold"
                style={{ color: 'var(--pd-crimson)', background: 'var(--pd-crimson-soft)' }}>Archive</button>
            </>
          ) : (
            <>
              {/* The inline Done buttons were promoted out of this hover
                  cluster above — keep the secondary actions only here so
                  the row doesn't render "Done" twice. */}
              {onSchedule && (
                <button data-testid={`decision-schedule-${decision.id}`}
                  onClick={(e) => { e.stopPropagation(); onSchedule(decision); }}
                  className="px-2 py-1 rounded-lg text-[10px] font-semibold"
                  style={{ color: 'var(--pd-focus)', background: 'var(--pd-blue-soft)' }}>Slot</button>
              )}
              {onDefer && decision.type === 'task' && (
                <button data-testid={`decision-defer-${decision.id}`}
                  onClick={(e) => { e.stopPropagation(); onDefer(decision); }}
                  className="px-2 py-1 rounded-lg text-[10px] font-semibold"
                  style={{ color: 'var(--pd-muted)', background: 'var(--pd-panel-2)' }}>Defer</button>
              )}
              {isTask && onFocus && (
                <button data-testid={`decision-focus-${decision.id}`}
                  onClick={(e) => { e.stopPropagation(); onFocus(decision); }}
                  className="px-2 py-1 rounded-lg text-[10px] font-semibold text-white"
                  style={{ background: 'var(--pd-focus)' }}>Focus</button>
              )}
              {isTask && !isDelegated && onDelegate && (
                <button data-testid={`decision-delegate-${decision.id}`}
                  onClick={(e) => { e.stopPropagation(); onDelegate(decision); }}
                  className="px-2 py-1 rounded-lg text-[10px] font-semibold"
                  style={{ color: 'var(--pd-warning)', background: 'rgba(180,83,9,0.08)' }}>Delegate</button>
              )}
              {decision.type === 'reading' && decision.url && (
                <a data-testid={`decision-open-${decision.id}`}
                  href={decision.url} target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="px-2 py-1 rounded-lg text-[10px] font-semibold flex items-center gap-0.5"
                  style={{ color: 'var(--pd-focus)', background: 'var(--pd-blue-soft)' }}>
                  <ExternalLink className="w-3 h-3" />Open
                </a>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
